import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../lib/metadata-builder/coloris";
import { artRgbColor } from "../../lib/schedule-art/art-color";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import { Input } from "../ui/input";

type TextColorPickerProps = {
  label: string;
  value: string;
  palette: string[];
  onChange: (color: string) => void;
};

export function TextColorPicker({ label, value, palette, onChange }: TextColorPickerProps) {
  const { t } = useTranslation();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(value);
  const applyColor = (text: string) => {
    const color = artRgbColor(text);
    if (!color) return null;
    const hex = "#" + [color.r, color.g, color.b].map(channel => channel.toString(16).padStart(2, "0")).join("");
    if (hex !== value) onChange(hex);
    return hex;
  };
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    window.Coloris({ swatches: palette, closeLabel: t("builder.color.close"),
      a11y: {
        open: t("builder.color.open"), close: t("builder.color.close"),
        marker: t("builder.color.marker"), hueSlider: t("builder.color.hue"),
        input: t("builder.color.value"), swatch: t("builder.color.swatch"),
        instruction: t("builder.color.instruction"),
      } });
  }, [palette, t]);
  useEffect(() => {
    const field = input.current;
    if (!field) return;
    const reposition = () => {
      if (document.querySelector(".clr-picker.clr-open")) window.Coloris.updatePosition();
    };
    field.addEventListener("close", endMetadataMerge);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.Coloris.close();
      field.removeEventListener("close", endMetadataMerge);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, []);

  return (
    <div className="grid min-w-0 content-start gap-1">
      <label htmlFor={id} className="text-xs font-medium">{label}</label>
      <div className="relative min-w-0">
        <Input ref={input} id={id} value={draft} type="text" className="builder-color-input h-8 pr-9 font-mono text-xs"
          onBlur={event => {
            setDraft(applyColor(event.currentTarget.value) ?? value);
            endMetadataMerge();
          }}
          onInput={event => {
            const text = event.currentTarget.value;
            setDraft(text);
            // Coloris emits complete hex values. Keep partial manual input editable until blur.
            if (/^#[0-9a-f]{6}$/i.test(text)) applyColor(text);
          }} />
        <button type="button" title={t("builder.color.open")} onClick={() => input.current?.click()}
          className="absolute top-1/2 right-2 size-5 -translate-y-1/2 rounded border border-neutral-300"
          style={{ backgroundColor: value }} />
      </div>
    </div>
  );
}
